import React, { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import { login, setAuthToken, resendVerificationEmail } from "../api/auth";
import toast from "react-hot-toast";
import TextInput from "./ui/TextInput";
import Button from "./ui/Button";

export default function LoginForm({ onLogin }) {
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", rememberMe: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Verification state
  const [needsVerification, setNeedsVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Countdown timer effect
  useEffect(() => {
    let interval;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await login(form);
      const { token, username } = res.data;
      setAuthToken(token);
      if (form.rememberMe) {
        localStorage.setItem("authToken", token);
      } else {
        sessionStorage.setItem("authToken", token);
      }
      setUser({ username });
      onLogin && onLogin({ token, username });
    } catch (err) {
      const errorData = err.response?.data;

      if (errorData?.needsVerification === true) {
        setNeedsVerification(true);
        setUnverifiedEmail(errorData.email || form.email);
        toast.error("Please verify your email before logging in");
      } else {
        toast.error(errorData?.error || "Login failed");
        setError(errorData?.error || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (countdown > 0) {
      toast.error(`Please wait ${countdown} seconds before resending`);
      return;
    }

    setResendLoading(true);
    try {
      await resendVerificationEmail({ email: unverifiedEmail });
      toast.success("Verification email sent! Please check your inbox.");
      setCountdown(300); // 5 minutes countdown (300 seconds)
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to resend verification email";
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setNeedsVerification(false);
    setUnverifiedEmail("");
    setCountdown(0);
    setError("");
    setForm({ ...form, password: "" }); // Clear password for security
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Verification Screen
  if (needsVerification) {
    return (
      <div className="flex flex-col gap-5 items-center w-full">
        <div className="text-center">
          <div className="text-5xl mb-3">📧</div>
          <h3 className="font-heading font-bold text-lg gg-gradient-text mb-1">
            Email Verification Required
          </h3>
          <p className="text-white/80 text-sm mb-1">
            Please verify your email address to continue.
          </p>
          <p className="text-brand-gold text-xs">{unverifiedEmail}</p>
        </div>

        <div className="w-full rounded-xl border border-brand-gold bg-brand-gold/10 text-white text-sm px-4 py-3">
          A verification link was sent to your email address. Please check your inbox and
          spam folder.
        </div>

        <div className="flex flex-col gap-3 w-full">
          <Button
            variant="primary"
            className="w-full"
            onClick={handleResendVerification}
            disabled={resendLoading || countdown > 0}
          >
            {resendLoading && (
              <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            )}
            {resendLoading
              ? "Sending..."
              : countdown > 0
                ? `Resend available in ${formatTime(countdown)}`
                : "🔄 Resend Verification Email"}
          </Button>

          <Button variant="outline" className="w-full" onClick={handleBackToLogin}>
            ← Back to Login
          </Button>
        </div>
      </div>
    );
  }

  // Normal Login Form
  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-5 items-center w-full">
      <TextInput
        name="email"
        type="email"
        label="Email"
        placeholder="you@example.com"
        icon="📧"
        value={form.email}
        onChange={handleChange}
        required
      />

      <TextInput
        name="password"
        type={showPassword ? "text" : "password"}
        label="Password"
        placeholder="••••••••"
        icon="🔒"
        value={form.password}
        onChange={handleChange}
        required
        endAdornment={
          <button
            type="button"
            onClick={handleClickShowPassword}
            aria-label="toggle password visibility"
            className="text-white/50 hover:text-brand-gold transition-colors p-1"
          >
            {showPassword ? "🙈" : "👁"}
          </button>
        }
      />

      <div className="w-full flex items-center justify-between">
        <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            name="rememberMe"
            checked={form.rememberMe}
            onChange={handleChange}
            className="h-4 w-4 rounded accent-brand-purple"
          />
          Remember Me
        </label>
      </div>

      <Button type="submit" variant="primary" size="lg" className="w-full" disabled={loading}>
        {loading ? (
          <>
            <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            Signing In...
          </>
        ) : (
          "Sign In →"
        )}
      </Button>

      {error && <p className="text-red-400 text-center text-sm mt-1">{error}</p>}
    </form>
  );
}
